import { useParams, Navigate } from 'react-router-dom'
import PageTransition from '@/components/ui/PageTransition'
import Section from '@/components/layout/Section'
import ProjectHero from '@/components/project/ProjectHero'
import ProjectInfo from '@/components/project/ProjectInfo'
import ProjectChallenge from '@/components/project/ProjectChallenge'
import ProjectSolution from '@/components/project/ProjectSolution'
import ProjectTechStack from '@/components/project/ProjectTechStack'
import ProjectMetrics from '@/components/project/ProjectMetrics'
import ProjectProcess from '@/components/project/ProjectProcess'
import ProjectNav from '@/components/ui/ProjectNav'
import VimeoEmbed from '@/components/ui/VimeoEmbed'
import ScrollReveal from '@/components/ui/ScrollReveal'
import { getProjectBySlug, getAdjacentProjects } from '@/data/projects'

export default function ProjectPage() {
  const { slug } = useParams<{ slug: string }>()
  const project = slug ? getProjectBySlug(slug) : undefined

  if (!project) {
    return <Navigate to="/" replace />
  }

  const { prev, next } = getAdjacentProjects(project.slug)
  const hasMultipleVideos = project.videoIds.length > 1
  const additionalVideoIds = hasMultipleVideos
    ? project.videoIds
    : project.videoIds.filter((id) => id !== project.previewVideoId)

  return (
    <PageTransition>
      <Section className="pt-8 md:pt-12">
        <ProjectHero project={project} showVideo={!hasMultipleVideos} />
      </Section>

      {hasMultipleVideos && (
        <Section className="pt-2 pb-2 md:pt-4 md:pb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {additionalVideoIds.map((videoId) => (
              <ScrollReveal key={videoId}>
                <VimeoEmbed videoId={videoId} />
              </ScrollReveal>
            ))}
          </div>
        </Section>
      )}

      <Section className="pt-6 pb-12 md:py-16">
        <ProjectInfo project={project} />
      </Section>

      {project.challenge && (
        <Section className="pb-12 md:pb-16">
          <ProjectChallenge challenge={project.challenge} sectionNumber="01" />
        </Section>
      )}

      {project.solution && (
        <Section className="pb-12 md:pb-16">
          <ProjectSolution solution={project.solution} sectionNumber="02" />
        </Section>
      )}

      {project.techStack && project.techStack.length > 0 && (
        <Section className="pb-12 md:pb-16">
          <ProjectTechStack techStack={project.techStack} sectionNumber="03" />
        </Section>
      )}

      {project.metrics && project.metrics.length > 0 && (
        <Section className="pb-12 md:pb-16">
          <ProjectMetrics metrics={project.metrics} sectionNumber="04" />
        </Section>
      )}

      {project.processImages && project.processImages.length > 0 && (
        <Section className="pb-12 md:pb-16">
          <ProjectProcess images={project.processImages} sectionNumber="05" />
        </Section>
      )}

      <Section>
        <ProjectNav prevProject={prev} nextProject={next} />
      </Section>
    </PageTransition>
  )
}
